/**
 * Daten der Zeitverteilung (F-28) fuer einen Zeitraum, live aus der
 * Datenbank: Vorkommen samt Bestaetigungen (F-29), Schlaf aus dem
 * Morgenjournal, Stoppuhr-Lernzeiten, Schul-Bereich, Standardschlaf und
 * Training ohne Kalendertermin (F-34 E2).
 */
import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '@/db/schema';
import { getOccurrences } from '@/db/repositories/events';
import { setMeta } from '@/db/repositories/meta';
import { zeitverteilung, type ZeitModus, type ZeitVerteilung } from '@/domain/zeit/verteilung';
import { STANDARD_TAGESRAHMEN, tagesrahmenAus, type Tagesrahmen } from '@/domain/zeit/fenster';
import { trainingsBloecke } from '@/domain/training/zeit';
import { addDays, now, parseLocal, toLocalDateTime } from '@/lib/datetime';
import type { CalendarOccurrence, ID, LocalDate, LocalDateTime } from '@/domain/types';

export const META_STANDARD_SCHLAF = 'standardSchlaf';
export const STANDARD_SCHLAF = 8;

interface Daten {
  termine: CalendarOccurrence[];
  schlaf: Map<LocalDate, number>;
  lernzeiten: { start: LocalDateTime; end: LocalDateTime }[];
  schuleBereich: ID | null;
  standardSchlaf: number;
}

const LEER: Daten = { termine: [], schlaf: new Map(), lernzeiten: [], schuleBereich: null, standardSchlaf: STANDARD_SCHLAF };

export function useZeitDaten(von: LocalDate, bis: LocalDate): Daten {
  return useLiveQuery(async () => {
    const ende = addDays(bis, 1);
    const [termine, morgen, sitzungen, schule, standard, protokolle, training] = await Promise.all([
      getOccurrences({ from: `${von}T00:00` as LocalDateTime, to: `${ende}T00:00` as LocalDateTime }),
      db.journal.where('date').between(von, bis, true, true).filter((e) => e.type === 'morgen' && e.sleepHours !== undefined).toArray(),
      db.studySessions.where('start').between(parseLocal(addDays(von, -1)).toISOString(), parseLocal(ende).toISOString(), true, false).toArray(),
      db.schoolSettings.get('schule'),
      db.meta.get(META_STANDARD_SCHLAF),
      db.trainingsprotokolle.where('datum').between(addDays(von, -1), bis, true, true).toArray(),
      db.trainingsEinstellungen.get('standard'),
    ]);
    const jetzt = now();
    return {
      // Training ohne Kalendertermin (F-34 E2) zaehlt im Sport-Bereich mit.
      termine: [...termine, ...trainingsBloecke(protokolle, termine, training?.sportBereichId ?? null)],
      schlaf: new Map(morgen.map((e) => [e.date, e.sleepHours!])),
      lernzeiten: sitzungen.map((s) => ({
        start: toLocalDateTime(new Date(s.start)),
        end: s.end ? toLocalDateTime(new Date(s.end)) : jetzt,
      })),
      schuleBereich: schule?.areaId ?? null,
      standardSchlaf: typeof standard?.value === 'number' ? standard.value : STANDARD_SCHLAF,
    };
  }, [von, bis], LEER);
}

export function useZeitverteilung(tage: LocalDate[], modus: ZeitModus): ZeitVerteilung & { standardSchlaf: number } {
  const von = tage[0]!;
  const bis = tage[tage.length - 1]!;
  const d = useZeitDaten(von, bis);
  const schluessel = tage.join();
  return useMemo(
    () => ({ ...zeitverteilung({ ...d, tage: schluessel.split(','), jetzt: now(), modus }), standardSchlaf: d.standardSchlaf }),
    [d, schluessel, modus],
  );
}

export function useStandardSchlaf(): number {
  return useLiveQuery(async () => {
    const v = (await db.meta.get(META_STANDARD_SCHLAF))?.value;
    return typeof v === 'number' ? v : STANDARD_SCHLAF;
  }, [], STANDARD_SCHLAF);
}

export async function setStandardSchlaf(stunden: number): Promise<void> {
  if (!Number.isFinite(stunden) || stunden < 0 || stunden > 16) return;
  await setMeta(META_STANDARD_SCHLAF, Math.round(stunden * 2) / 2);
}

/**
 * Tagesrahmen (F-50): wach von-bis. Die Agenda der Uebersicht zeigt freie
 * Fenster nur darin. Wie der Standardschlaf je Geraet gespeichert.
 */
export const META_TAGESRAHMEN = 'tagesrahmen';

export function useTagesrahmen(): Tagesrahmen {
  return useLiveQuery(async () => tagesrahmenAus((await db.meta.get(META_TAGESRAHMEN))?.value), [], STANDARD_TAGESRAHMEN);
}

export async function setTagesrahmen(r: Tagesrahmen): Promise<void> {
  const geprueft = tagesrahmenAus(r);
  if (geprueft.von !== r.von || geprueft.bis !== r.bis) return;
  await setMeta(META_TAGESRAHMEN, geprueft);
}
